// src/services/cotizadorService.js
// Acceso a Supabase del Cotizador BV (solo admin; las tablas tienen RLS por
// correo, ver 20261024000000_cotizador_bv.sql). Las horas de Postgres llegan
// como 'HH:MM:SS' y se normalizan a 'HH:MM' para los pickers nativos.
import { supabase } from './supabase';
import { AJUSTES_DEFECTO, HORARIOS_DEFECTO, REVENDEDOR_DEFECTO, diasAutomaticos } from './cotizadorCalc';

const hm = (v) => (v ? String(v).slice(0, 5) : null);
const numOrNull = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
export const hoyIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CAMPOS_NUM_OPCION = [
  'comprarviagem_lista', 'speedmilhas', 'resumen_pedido', 'millas_cantidad',
  'milheiro', 'taxa', 'omnibus_total_manual', 'otro_extra',
  'precio_final_manual', 'ganancia_minima_opcion', 'escalas', 'llegada_mas_dias',
];

const normOpcion = (o) => ({
  ...o,
  hora_vuelo: hm(o.hora_vuelo),
  hora_llegada_vuelo: hm(o.hora_llegada_vuelo),
  extras: Array.isArray(o.extras) ? o.extras : [],
  aerolinea: o.aerolinea || '',
  ...Object.fromEntries(CAMPOS_NUM_OPCION.map((k) => [k, numOrNull(o[k])])),
});

// Prepara un parche para guardar: '' -> null en números y horas.
const limpiarOpcion = (patch) => {
  const out = { ...patch };
  CAMPOS_NUM_OPCION.forEach((k) => { if (k in out) out[k] = numOrNull(out[k]); });
  ['hora_vuelo', 'hora_llegada_vuelo'].forEach((k) => { if (k in out) out[k] = out[k] || null; });
  if (Array.isArray(out.extras)) out.extras = out.extras.map((e) => ({ ...e, total: numOrNull(e.total) }));
  return out;
};

// ── Ajustes ──────────────────────────────────────────────────────────
export async function getAjustes() {
  const { data, error } = await supabase.from('cotizador_ajustes').select('*').eq('id', 1).maybeSingle();
  if (error || !data) return { ...AJUSTES_DEFECTO, mostrar_herramientas_ocultas: false };
  const out = { ...AJUSTES_DEFECTO, mostrar_herramientas_ocultas: !!data.mostrar_herramientas_ocultas };
  Object.keys(AJUSTES_DEFECTO).forEach((k) => {
    if (data[k] === null || data[k] === undefined) return;
    if (k === 'niveles') out[k] = (data[k] || []).map(Number);
    else if (k === 'hora_vuelo_defecto') out[k] = hm(data[k]);
    else out[k] = Number(data[k]);
  });
  return out;
}

export async function guardarAjustes(patch) {
  const { error } = await supabase
    .from('cotizador_ajustes')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', 1);
  if (error) throw error;
}

// Solo el interruptor de herramientas ocultas (lo lee el panel de admin).
// Si no se puede leer (por ejemplo, la migración aún no se aplicó) se
// muestran todas: así nunca queda una pestaña escondida sin forma de volver.
export async function getMostrarHerramientasOcultas() {
  const { data, error } = await supabase.from('cotizador_ajustes').select('mostrar_herramientas_ocultas').eq('id', 1).maybeSingle();
  if (error || !data) return true;
  return data.mostrar_herramientas_ocultas === true;
}

// ── Horarios del ómnibus ─────────────────────────────────────────────
export async function listarHorarios() {
  const { data, error } = await supabase.from('cotizador_horarios').select('*').order('orden');
  if (error || !data || !data.length) return error ? HORARIOS_DEFECTO : data || [];
  return data.map((h) => ({ ...h, sale: hm(h.sale), llega: hm(h.llega), precio: numOrNull(h.precio), mas_dias: Number(h.mas_dias) }));
}

export async function guardarHorario(h) {
  const fila = {
    orden: h.orden, nombre: h.nombre || `${h.sale} horario`, sale: h.sale, llega: h.llega,
    mas_dias: Number(h.mas_dias) || 0, precio: numOrNull(h.precio), activo: h.activo !== false, nota: h.nota || '',
    empresa: h.empresa || '', terminal_origen: h.terminal_origen || '', terminal_destino: h.terminal_destino || '',
  };
  const q = h.id
    ? supabase.from('cotizador_horarios').update(fila).eq('id', h.id)
    : supabase.from('cotizador_horarios').insert(fila);
  const { error } = await q;
  if (error) throw error;
}

export async function borrarHorario(id) {
  const { error } = await supabase.from('cotizador_horarios').delete().eq('id', id);
  if (error) throw error;
}

// ── Cotizaciones ─────────────────────────────────────────────────────
// Lista con sus opciones para poder mostrar la mejor ganancia de cada una.
export async function listarCotizaciones() {
  const [{ data: cots, error: e1 }, { data: ops, error: e2 }, revs] = await Promise.all([
    supabase.from('cotizador_cotizaciones').select('*').order('created_at', { ascending: false }),
    supabase.from('cotizador_opciones').select('*').order('orden'),
    listarRevendedores(),
  ]);
  if (e1 || e2) throw e1 || e2;
  return (cots || []).map((c) => ({
    ...c,
    pasajeros: Array.isArray(c.pasajeros) ? c.pasajeros : [], pagado: !!c.pagado,
    tipo: c.tipo || 'directo',
    revendedor: revs.find((r) => r.id === c.revendedor_id) || null,
    usd: Number(c.usd), tasa: Number(c.tasa), ganancia_minima: numOrNull(c.ganancia_minima),
    opciones: (ops || []).filter((o) => o.cotizacion_id === c.id).map(normOpcion),
  }));
}

export async function obtenerCotizacion(id) {
  const [{ data: c, error: e1 }, { data: ops, error: e2 }, revs] = await Promise.all([
    supabase.from('cotizador_cotizaciones').select('*').eq('id', id).single(),
    supabase.from('cotizador_opciones').select('*').eq('cotizacion_id', id).order('orden'),
    listarRevendedores(),
  ]);
  if (e1 || e2) throw e1 || e2;
  return {
    ...c, pasajeros: Array.isArray(c.pasajeros) ? c.pasajeros : [], pagado: !!c.pagado,
    tipo: c.tipo || 'directo', revendedor: revs.find((r) => r.id === c.revendedor_id) || null,
    usd: Number(c.usd), tasa: Number(c.tasa), ganancia_minima: numOrNull(c.ganancia_minima),
    opciones: (ops || []).map(normOpcion),
  };
}

// ── Revendedores ─────────────────────────────────────────────────────
// Si la tabla aún no existe (migración sin aplicar) devuelve [] y todo
// sigue funcionando con clientes directos.
export async function listarRevendedores() {
  const { data, error } = await supabase.from('revendedores').select('*').order('nombre');
  if (error || !data) return [];
  return data.map((r) => ({
    ...r, margen_pct: Number(r.margen_pct), ganancia_minima: Number(r.ganancia_minima), redondeo: Number(r.redondeo),
  }));
}

export async function crearRevendedor(nombre) {
  const { data, error } = await supabase.from('revendedores').insert({ nombre, ...REVENDEDOR_DEFECTO }).select('*').single();
  if (error) throw error;
  return { ...data, margen_pct: Number(data.margen_pct), ganancia_minima: Number(data.ganancia_minima), redondeo: Number(data.redondeo) };
}

export async function actualizarRevendedor(id, patch) {
  const limpio = { ...patch };
  if ('redondeo' in limpio && !(Number(limpio.redondeo) > 0)) delete limpio.redondeo;
  const { error } = await supabase.from('revendedores').update(limpio).eq('id', id);
  if (error) throw error;
}

// Nueva = siempre en cero: US$ y tasa de Ajustes, llegada hoy, 6 filas vacías
// (llegada + 2 … llegada + 7). Nada se hereda de otra cotización.
export async function crearCotizacion(ajustes, tipo = 'directo') {
  const llegada = hoyIso();
  const { data: c, error } = await supabase
    .from('cotizador_cotizaciones')
    .insert({
      nombre: '', llegada, usd: ajustes.usd_defecto, tasa: ajustes.tasa_defecto,
      ...(tipo === 'revendedor' ? { tipo } : {}),
    })
    .select('id')
    .single();
  if (error) throw error;
  const filas = diasAutomaticos(llegada).map((dia, i) => ({ cotizacion_id: c.id, orden: i, dia_vuelo: dia }));
  const { error: e2 } = await supabase.from('cotizador_opciones').insert(filas);
  if (e2) throw e2;
  return c.id;
}

export async function duplicarCotizacion(id) {
  const orig = await obtenerCotizacion(id);
  const { data: c, error } = await supabase
    .from('cotizador_cotizaciones')
    .insert({
      nombre: orig.nombre ? `${orig.nombre} (copia)` : '', llegada: orig.llegada,
      usd: orig.usd, tasa: orig.tasa, ganancia_minima: orig.ganancia_minima,
      ...(orig.tipo === 'revendedor' ? { tipo: orig.tipo, revendedor_id: orig.revendedor_id } : {}),
    })
    .select('id')
    .single();
  if (error) throw error;
  if (orig.opciones.length) {
    // created_at se conserva: el historial cuenta cuándo se cotizó de verdad.
    const filas = orig.opciones.map(({ id: _id, cotizacion_id: _c, ...resto }, i) => ({
      ...resto, cotizacion_id: c.id, orden: i,
    }));
    const { error: e2 } = await supabase.from('cotizador_opciones').insert(filas);
    if (e2) throw e2;
  }
  return c.id;
}

export async function borrarCotizacion(id) {
  const { error } = await supabase.from('cotizador_cotizaciones').delete().eq('id', id);
  if (error) throw error;
}

export async function actualizarCotizacion(id, patch) {
  const limpio = { ...patch, updated_at: new Date().toISOString() };
  if ('ganancia_minima' in limpio) limpio.ganancia_minima = numOrNull(limpio.ganancia_minima);
  delete limpio.revendedor; // objeto calculado, no es columna
  const { error } = await supabase.from('cotizador_cotizaciones').update(limpio).eq('id', id);
  if (error) throw error;
}

// Si cambia el día de llegada, las filas automáticas se corren con él.
export async function moverFilasPorLlegada(opciones, nuevasFechas) {
  await Promise.all(opciones.map((o, i) => (
    nuevasFechas[i] ? supabase.from('cotizador_opciones').update({ dia_vuelo: nuevasFechas[i] }).eq('id', o.id) : null
  )));
}

export async function actualizarOpcion(id, patch) {
  const { error } = await supabase.from('cotizador_opciones').update(limpiarOpcion(patch)).eq('id', id);
  if (error) throw error;
}

export async function agregarOpcion(cotizacionId, orden, diaVuelo) {
  const { data, error } = await supabase
    .from('cotizador_opciones')
    .insert({ cotizacion_id: cotizacionId, orden, dia_vuelo: diaVuelo })
    .select('*')
    .single();
  if (error) throw error;
  return normOpcion(data);
}

// Crea filas nuevas justo debajo de `original` para cotizar OTROS vuelos del mismo día (el más
// barato y el más cómodo, por ejemplo). Cada fila se queda con lo que no cambia (día, ciudad,
// ómnibus manual, extras, traslado) y vacía lo que es de un vuelo en concreto (horas, aerolínea,
// escalas, precios, número...); `parches` trae lo que sí se sabe de cada vuelo (por ejemplo, lo
// leído de las capturas). Las filas de abajo bajan para dejar sitio.
export async function crearOpcionesDespues(cotizacionId, original, opcionesActuales, parches) {
  const idx = opcionesActuales.findIndex((o) => o.id === original.id);
  const base = {
    cotizacion_id: cotizacionId, dia_vuelo: original.dia_vuelo, ciudad: original.ciudad || '',
    omnibus_total_manual: numOrNull(original.omnibus_total_manual), otro_extra: numOrNull(original.otro_extra),
    extras: Array.isArray(original.extras) ? original.extras : [], traslado_incluido: !!original.traslado_incluido,
    aeropuerto_salida: original.aeropuerto_salida || '', aeropuerto_llegada: original.aeropuerto_llegada || '',
    sale_de: original.sale_de === 'BVB' ? 'BVB' : 'MAO',
  };
  const filas = parches.map((p, i) => ({ ...base, ...limpiarOpcion(p || {}), orden: idx + 1 + i }));
  const { data, error } = await supabase.from('cotizador_opciones').insert(filas).select('*');
  if (error) throw error;
  const nuevas = (data || []).sort((a, b) => a.orden - b.orden).map(normOpcion);
  const siguientes = opcionesActuales.slice(idx + 1);
  await Promise.all(siguientes.map((o, i) => supabase.from('cotizador_opciones').update({ orden: idx + 1 + parches.length + i }).eq('id', o.id)));
  return nuevas;
}

export async function duplicarOpcion(cotizacionId, original, opcionesActuales) {
  return (await crearOpcionesDespues(cotizacionId, original, opcionesActuales, [{}]))[0];
}

export async function borrarOpcion(id) {
  const { error } = await supabase.from('cotizador_opciones').delete().eq('id', id);
  if (error) throw error;
}

// ── Historial de precios importado ───────────────────────────────────
export async function listarHistorialImportado() {
  const { data, error } = await supabase.from('cotizador_historial').select('*').order('fecha_cotizacion', { ascending: false });
  if (error || !data) return [];
  return data.map((h) => ({
    destino: h.destino, costo: Number(h.costo), fechaCot: h.fecha_cotizacion, fechaVuelo: h.fecha_vuelo,
    aerolinea: h.aerolinea || '', fuente: h.fuente || '', escalas: h.escalas === null ? null : Number(h.escalas), origen: 'csv',
  }));
}

// Inserta solo lo que todavía no está (por `clave`). Devuelve cuántas filas
// entraron y cuántas ya existían.
export async function importarHistorial(registros) {
  const { data: previas, error: e0 } = await supabase.from('cotizador_historial').select('clave');
  if (e0) throw e0;
  const ya = new Set((previas || []).map((p) => p.clave));
  const nuevas = registros.filter((r) => !ya.has(r.clave)).map((r) => ({
    clave: r.clave, destino: r.destino, ruta: r.ruta, fecha_cotizacion: r.fechaCot, fecha_vuelo: r.fechaVuelo || null,
    aerolinea: r.aerolinea, fuente: r.fuente, escalas: r.escalas, costo: r.costo,
  }));
  for (let i = 0; i < nuevas.length; i += 200) {
    const { error } = await supabase.from('cotizador_historial').insert(nuevas.slice(i, i + 200));
    if (error) throw error;
  }
  return { nuevas: nuevas.length, repetidas: registros.length - nuevas.length };
}
